import { Component } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { CRM_ERROR_REQUIRED_WORK } from 'app/crm/crm-errors';
import { CRM_QUOTE_STAGE_LABELS } from 'app/crm/crm-stage-appearance';

/**
 * Losing a deal, said in full before it happens (CRM-175).
 *
 * On a quote-connected pipeline a deal is Lost because no quote is left in play, so marking it
 * Lost cancels the ones that are. That is a change to the customer's paperwork, not a stage, so
 * this names every quote and change order it will cancel, what is already billed against them,
 * and — when the deal cannot be lost — why, before anybody types a reason. The list comes from
 * the server's own plan (`crm/lose_preview`), the same reading `crm/lose_deal` acts on, so the
 * dialog cannot promise something the action then refuses.
 *
 * Two ways in:
 *
 *  - open({ crmId }) — Mark as Lost on the deal page, a drop on the board's Lost lane, or a pick
 *    from the stage menu. Asks Needs your attention first, as a drag does, then shows the plan and
 *    loses the deal itself. Closes with { lost: result }.
 *  - open({ quote: true, action, title, stage, billed, currencyId }) — the last quote in play on a deal being
 *    cancelled or deleted on the quote screen, or unlinked on the deal page (`action` is 'cancel',
 *    'delete' or 'unlink'). Only the reason is asked: the change is the caller's own, and it
 *    resends with the reason. Closes with { reason }. `billed` is what is invoiced against that
 *    quote, from the refusal's payload, so it is named here just as Mark as Lost names it.
 *
 * Closing any other way — Cancel, the cross, Escape — closes with null, and nothing has changed.
 */
@Component({
	selector: 'app-modal-crm-lose',
	templateUrl: './modal-crm-lose.component.html'
})
export class ModalCrmLoseComponent extends UIModalBaseComponent {

	// Matches crm.lost_reason (CRM::LOST_REASON_MAX).
	readonly maxLength = 255;

	readonly stageLabels = CRM_QUOTE_STAGE_LABELS;

	quoteMode = false;
	action: 'cancel' | 'delete' | 'unlink' = 'cancel';
	quoteBilled: any = null;
	quoteCurrency = null;
	crmId = null;
	title = '';
	stage = 'Lost';
	preview: any = null;
	reason = '';
	loading = false;
	disabled = false;

	constructor(
		public app: AppService,
		private api: ApiService
	) {
		super();
	}

	get quotes(): any[] {
		return this.preview?.quotes || [];
	}

	get blocked(): string {
		return this.preview?.blocked || '';
	}

	get quoteConnected() {
		return !!Number(this.preview?.quote_connected);
	}

	// Whether Reopen would succeed, from the server's own reopen plan — not inferred from a signed quote.
	get reopenable() {
		return !!Number(this.preview?.reopenable);
	}

	get signedQuotes() {
		return this.quotes.filter(q => q.stage === 'accepted' || q.stage === 'complete');
	}

	get billedQuotes() {
		return this.quotes.filter(q => Number(q.billed?.invoice_count) > 0);
	}

	/**
	 * One list for the invoice warning in both modes: the deal's quotes, or the one quote being
	 * changed. Invoices are never touched by any of this, so the warning is about what is left
	 * standing, and it is said before the click in every place that can lose a deal.
	 */
	get invoiceRows(): any[] {
		if (!this.quoteMode) return this.billedQuotes;
		return Number(this.quoteBilled?.invoice_count) > 0
			? [{ quote_no: null, billed: this.quoteBilled, currency_id: this.quoteCurrency }]
			: [];
	}

	get invoiceNote() {
		const doing = this.quoteMode ? { cancel: 'Cancelling', delete: 'Deleting', unlink: 'Unlinking' }[this.action] : 'Cancelling';
		return doing + ' the quote does not change the invoices — they stay exactly as they are'
			+ (doing === 'Cancelling' ? ', raised against a cancelled quote' : '')
			+ '. Credit or void them yourself if that is what you mean to happen.';
	}

	get hasChangeOrders() {
		return this.quotes.some(q => !!q.change_order);
	}

	// Quotes rather than rows: a change order is part of the quote it changes (D-078).
	get quoteCount() {
		return this.quotes.filter(q => !q.change_order).length || this.quotes.length;
	}

	get canConfirm() {
		return !this.loading && !this.disabled && !this.blocked && !!(this.reason || '').trim();
	}

	open(data: any) {
		this.quoteMode = !!data?.quote;
		this.action = data?.action || 'cancel';
		this.quoteBilled = data?.billed || null;
		this.quoteCurrency = data?.currencyId || null;
		this.crmId = data?.crmId ?? null;
		this.title = data?.title || '';
		this.stage = data?.stage || 'Lost';
		this.preview = null;
		this.reason = '';
		this.disabled = false;

		if (this.quoteMode) {
			this.loading = false;
			this.modal.open();
			return;
		}

		/**
		 * The checks first, then the dialog — the order Mark as Won uses. Losing closes the deal, so
		 * it is the last moment the pipeline's own checklist is worth anything, and being asked
		 * for a reason only to be sent off to clear a checklist is being asked twice. Backing out
		 * of that dialog backs out of the loss.
		 */
		this.app.requiredWork.check(this.crmId, null, () => {
			this.loading = true;
			this.modal.open();

			this.api.crm.losePreview(this.crmId, res => {
				this.loading = false;
				this.preview = res;
				this.title = res?.title || this.title;
				this.stage = res?.stage || this.stage;
			}, error => {
				this.loading = false;
				this.modal.close(null, false);
				this.app.notifications.showDanger(error.message);
				this.close.emit(null);
			});
		}, 'marking this deal as Lost', () => this.close.emit(null));
	}

	// The verb for the quote-screen sentence and button: "deleting it", "Delete quote".
	get actionVerb() {
		return { cancel: 'cancelling', delete: 'deleting', unlink: 'unlinking' }[this.action];
	}

	get actionButton() {
		return { cancel: 'Cancel quote', delete: 'Delete quote', unlink: 'Unlink quote' }[this.action] + ' and mark Lost';
	}

	stageLabel(stage) {
		return this.stageLabels[stage] || stage;
	}

	stageBadge(stage) {
		if (stage === 'accepted' || stage === 'complete') return 'badge-success';
		if (stage === 'sent') return 'badge-info';
		return 'badge-secondary';
	}

	rowLabel(q) {
		if (q.change_order) return 'Change order ' + (q.change_order_no || q.change_order);
		return q.quote_no + (Number(q.revision) > 1 ? ' Rev ' + q.revision : '');
	}

	// A change order listed straight under its own quote is indented beneath it; one whose quote
	// is not being cancelled with it (an accepted change order is the contract) names the quote.
	underQuote(index) {
		const q = this.quotes[index];
		const prev = this.quotes[index - 1];
		return !!q?.change_order && !!prev && prev.family_id === q.family_id;
	}

	cancel() {
		this.modal.close(null);
	}

	confirm() {
		const reason = (this.reason || '').trim();
		if (!reason) {
			this.app.notifications.showDanger('Please give a reason for marking this deal lost.');
			return;
		}

		if (this.quoteMode) {
			this.modal.close({ reason });
			return;
		}

		this.disabled = true;
		this.lose(reason);
	}

	/**
	 * The loss itself. Required work was asked before the dialog opened, but something can appear
	 * in between — an automation running on another tab — so a hold is still met with the same
	 * dialog, and Continue there runs this again rather than something like it.
	 */
	private lose(reason) {
		this.api.crm.loseDeal({ id: this.crmId, reason }, res => {
			this.disabled = false;
			this.modal.close({ lost: res });
		}, error => {
			this.disabled = false;

			if (error?.code === CRM_ERROR_REQUIRED_WORK) {
				this.modal.close(null, false);
				this.app.requiredWork.check(this.crmId, null, () => this.lose(reason),
					'moving this deal to ' + this.stage, () => this.close.emit(null));
				return;
			}

			this.app.notifications.showDanger(error.message);
		});
	}

}
